import type { CalendarProject } from '../types/calendarProject';
import type { JobTicket } from '../types/jobTicket';

const productNames = {
  basic_year_story: 'Basic Year Story',
  family_planner_cz: 'Family Planner CZ',
  fineart_gallery: 'FineArt Gallery',
};

function createJobId(projectId: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `job_${crypto.randomUUID()}`;
  }

  return `job_${projectId}_${Date.now().toString(36)}`;
}

export function getProjectJsonFilename(projectId: string) {
  return `calendar-studio-project-${projectId}.json`;
}

export function getPreflightReportFilename(projectId: string) {
  return `calendar-studio-preflight-report-${projectId}.json`;
}

export function getJobTicketFilename(projectId: string) {
  return `calendar-studio-job-ticket-${projectId}.json`;
}

export function createJobTicket(project: CalendarProject): JobTicket {
  const report = project.preflight.lastReport;
  const localAssetCount = project.photos.filter((photo) => photo.hasLocalAsset === true).length;
  const cloudAssetCount = project.photos.filter((photo) => photo.cloudAsset?.objectKey || photo.uploadStatus === 'uploaded').length;
  const assetStorageProductionNotes = [
    'Originály jsou v MVP uloženy pouze lokálně v IndexedDB.',
    'Project JSON neobsahuje binární data.',
    'Pro ostrou výrobu je nutné doplnit Cloudflare R2 upload.',
    'HTML print preview není finální produkční PDF/X.',
  ];

  return {
    schemaVersion: '0.1',
    jobId: createJobId(project.id),
    projectId: project.id,
    createdAt: new Date().toISOString(),
    productId: project.productId,
    productName: productNames[project.productId],
    locale: project.locale,
    currency: project.currency,
    format: 'A3',
    pagesCount: project.pages.length,
    period: {
      mode: project.period.mode,
      year: project.period.year,
      startMonth: project.period.startMonth,
    },
    themeId: project.themeId,
    print: {
      bleedMm: 3,
      safeZoneMm: 5,
      targetDpi: 300,
      colorMode: 'CMYK-ready-placeholder',
      pdfProfile: 'PDF/X-ready-placeholder',
    },
    files: {
      projectJson: getProjectJsonFilename(project.id),
      preflightReport: getPreflightReportFilename(project.id),
      productionPdf: 'not-generated-in-mvp',
      previewPdf: 'not-generated-in-mvp',
      assetsPackage: 'not-generated-in-mvp',
    },
    preflight: {
      status: project.preflight.status,
      warnings: report?.warningCount ?? project.preflight.issues.filter((issue) => issue.severity === 'warning').length,
      errors: report?.errorCount ?? project.preflight.issues.filter((issue) => issue.severity === 'error').length,
    },
    assetStorage: {
      mode: 'indexeddb-local-mvp',
      localAssetCount,
      cloudAssetCount,
      productionAssetPackageReady: false,
      productionNotes: assetStorageProductionNotes,
    },
    productionNotes: [
      'MVP export bez binárních assetů',
      'Lokální assety jsou uloženy pouze v IndexedDB tohoto prohlížeče',
      'Project JSON neobsahuje binární data',
      'Produkční verze bude vyžadovat cloud upload originálních fotek',
      'HTML print preview není finální PDF/X',
      'Browser print dialog může změnit měřítko, pokud není nastaveno 100 %',
      'Produkční PDF/X bude samostatný exportní krok',
    ],
  };
}
